import React from 'react';

import Button from '@amzn/meridian/button';
import { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import useForm, { type Validations } from 'src/hooks/useForm';
import {
  MAX_TASK_DESCRIPTION_CHAR_LENGTH,
  MAX_TASK_NAME_CHAR_LENGTH,
} from 'src/store/adminConfigPageSlice/common/constants';
import {
  addOrEditTask,
  setIsTaskModalOpen,
  setSelectedTaskTableRow,
  type TaskTableDataRow,
} from 'src/store/adminConfigPageSlice/taskConfigPageSlice/taskConfigPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';

import TaskModalForm from './taskModalForm';

// **** Constants **** //

const VALIDATIONS: Validations<TaskTableDataRow> = {
  label: {
    required: {
      value: true,
      message: 'Task name is required',
    },
    custom: {
      isValid: (value) => value.length <= MAX_TASK_NAME_CHAR_LENGTH,
      message: `Task name must be less than or equal to ${MAX_TASK_NAME_CHAR_LENGTH} characters.`,
    },
  },
  regions: {
    required: {
      value: true,
      message: 'Region is required',
    },
  },
  isMeasuredBy: {
    required: {
      value: true,
      message: 'Unit of measurement is required',
    },
  },
  description: {
    required: {
      value: false,
    },
    custom: {
      isValid: (value) => value.length <= MAX_TASK_DESCRIPTION_CHAR_LENGTH,
      message: `Description must be less than or equal to ${MAX_TASK_DESCRIPTION_CHAR_LENGTH} characters.`,
    },
  },
};

// **** Components **** //

/**
 * TaskModal Content
 */
const TaskModalContent = () => {
  const dispatch = useAppDispatch();
  const { selectedTaskTableRow } = useAppSelector((state) => state.taskConfigPage);

  // Setup "useForm" hook
  const { validateForm, handleChange, resetForm, formData, errors } = useForm<TaskTableDataRow>({
    validations: { ...VALIDATIONS },
    initialValues: {
      id: selectedTaskTableRow?.id,
      regions: selectedTaskTableRow?.regions ?? [],
      label: selectedTaskTableRow?.label ?? '',
      description: selectedTaskTableRow?.description ?? '',
      isMeasuredBy: selectedTaskTableRow?.isMeasuredBy ?? '',
      identifyBodyLocation: selectedTaskTableRow?.identifyBodyLocation,
    },
  });

  const onSave = () => {
    if (validateForm()) {
      dispatch(addOrEditTask(formData));
      dispatch(setIsTaskModalOpen(false));
      dispatch(setSelectedTaskTableRow(null));
      resetForm();
    }
  };

  const onCancel = () => {
    dispatch(setIsTaskModalOpen(false));
    dispatch(setSelectedTaskTableRow(null));
    resetForm();
  };

  return (
    <>
      <TaskModalForm formData={formData} errors={errors} handleChange={handleChange} />
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fill">
          <Button className="view-and-edit-tasks__modal__cancel-btn" type="secondary" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" className="view-and-edit-tasks__modal__submit-btn" size="small" onClick={onSave}>
            Save
          </Button>
        </Row>
      </ModalFooter>
    </>
  );
};

// **** Export **** //

export default TaskModalContent;
